import { Injectable } from '@angular/core';
import {Observable, ReplaySubject, Subscription} from 'rxjs';
import { map } from 'rxjs/operators';
import { HttpService } from '@supoui/core/services/http.service';
import { SupplierProfile } from '@supoui/shared/model/supplier-profile';
import moment from 'moment';
import { SupplierProfileStateService } from '../cache/supplier-profile-state-service';
import { AccountService } from '../auth/account.service';
import { UntilDestroy } from '@ngneat/until-destroy';

@Injectable({
  providedIn: 'root'
})
@UntilDestroy({ checkProperties: true })
export class SupplierProfileService {
  private cachedAllSupplierProfiles = new ReplaySubject<SupplierProfile[]>(1);
  private cachedAllSupplierProfilesSize = 0;
  private cachedAllSupplierProfilesLoading = false;

  private cachedCurrentSupplierProfiles = new ReplaySubject<SupplierProfile[]>(1);
  private cachedCurrentSupplierProfilesSize = 0;
  private cachedCurrentSupplierProfilesLoading = false;

  private authSubscription: Subscription;

  constructor(private httpService: HttpService, private supplierProfileStateService: SupplierProfileStateService, private accountService: AccountService) {
    this.authSubscription = this.accountService.getAuthenticationState().subscribe(account => {
      this.resetCache();
    });
  }

  getAllSupplierProfiles(): Observable<SupplierProfile[]> {
    if (!this.cachedAllSupplierProfilesLoading && this.cachedAllSupplierProfilesSize === 0) {
      this.cachedAllSupplierProfilesLoading = true;
      this.httpService.get<SupplierProfile[]>(`services/supo-user-administration/api/supplier-profiles`)
                           .pipe(map(profiles => profiles.map(profile => new SupplierProfile(profile))
                           .filter(profile => this.isValid(profile))))
                           .subscribe(response => {
              this.cachedAllSupplierProfilesSize = response.length;
              this.cachedAllSupplierProfilesLoading = false;
              this.cachedAllSupplierProfiles.next(response);
      })
    }
    return this.cachedAllSupplierProfiles;
  }

  getSupplierProfiles(caseId?: string): Observable<SupplierProfile[]> {
    if ( !!caseId ) {
      if ( !this.supplierProfileStateService.isSupplierProfilesCached(caseId) ) {
        this.supplierProfileStateService.initSupplierProfilesCache(caseId);
        this.httpService.get<SupplierProfile[]>(`services/supo-user-administration/api/supplier-profiles/by-case/${caseId}`)
                              .pipe(map(profiles => profiles.map(profile => new SupplierProfile(profile))
                              .filter(profile => this.isValid(profile))))
                              .subscribe({
                                next: response => {
                                  this.supplierProfileStateService.emitResponse(response);
                                },
                                error: err => {
                                  this.supplierProfileStateService.emitError();
                                  this.supplierProfileStateService.clearCache();
                                  throw err;
                                }
                              })
      }
      return this.supplierProfileStateService.getSupplierProfiles();
    }
    else {
      if (!this.cachedCurrentSupplierProfilesLoading && this.cachedCurrentSupplierProfilesSize === 0) {
         this.httpService.get<SupplierProfile[]>(`services/supo-user-administration/api/supplier-profiles/current`)
                              .pipe(map(profiles => profiles.map(profile => new SupplierProfile(profile))
                              .filter(profile => this.isValid(profile))))
                              .subscribe(response => {
                                this.cachedCurrentSupplierProfilesSize = response.length;
                                this.cachedCurrentSupplierProfilesLoading = false;
                                this.cachedCurrentSupplierProfiles.next(response);
        })
      }
      return this.cachedCurrentSupplierProfiles;
    }
  }

  updateSupplierProfile(profile: SupplierProfile): Observable<void> {
    return this.httpService
      .put<void>('services/supo-user-administration/api/supplier-profiles', profile);
  }

  resetCache() {
    this.cachedAllSupplierProfilesSize = 0;
    this.cachedCurrentSupplierProfilesSize = 0;
    this.supplierProfileStateService.clearCache();
  }

  private isValid(profile): boolean {
    const currentDate = moment();
    if (profile.startDate && profile.endDate) {
      return currentDate.isBetween(moment(profile.startDate), moment(profile.endDate));
    } else if (profile.startDate) {
      return currentDate.isSameOrAfter(moment(profile.startDate));
    } else if (profile.endDate) {
      return currentDate.isSameOrBefore(moment(profile.endDate));
    } else {
      return !!profile;
    }
  }
}
